<template>
  <div
    data-test-id="sso-status-container"
  >
    <div
      :class="$style.noneDisplayWrapper"
      data-test-id="hiddenInputWrapper"
    >
      <input
        id="upload-cert"
        :ref="'uploadcertInput'"
        accept=".pem"
        data-key="idpCertificate"
        name="file"
        type="file"
        @change="handleDataKeyChanged"
      >
      <input
        id="upload-private-key"
        :ref="'uploadprivatekey'"
        accept=".pem"
        data-key="idpPrivateKey"
        name="file"
        type="file"
        @change="handleDataKeyChanged"
      >
    </div>
    <header :class="$style.header">
      Single sign-on
    </header>
    <ul :class="$style.list">
      <li :class="$style.statusItem">
        <Status v-bind="appSingleSignOnStatusProps" />
      </li>
      <li
        v-if="idpCertificateStatusProps"
        :class="$style.statusItem"
      >
        <Status v-bind="idpCertificateStatusProps" />
      </li>
      <li
        v-if="idpPrivateKeyCertificateStatusProps"
        :class="$style.statusItem"
      >
        <Status v-bind="idpPrivateKeyCertificateStatusProps" />
      </li>
    </ul>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { ApplicationSSOModel, EOL, EOS } from 'client/util/Constants/ApplicationConstants';
import { cloneDeep } from 'lodash';
import { isOIDC } from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';

import ApplicationConfigCertMixin from 'client/applications/mixins/ApplicationConfigCertMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';

import Status from 'components/Status/Status.vue';

import DateUtil from 'client/util/DateUtil';

const DATA_READY_STATE = 'ready';
const DATA_SAVING_STATE = 'saving';

const { StatusColorNames } = Constants;
const { ItemTypeConsts, StoreModelMap } = Constants;
const { application } = ItemTypeConsts;

const ApplicationModel = StoreModelMap[application];

export default {
  name: 'ApplicationSSOStatus',

  components: {
    Status,
  },

  mixins: [
    ApplicationConfigCertMixin,
  ],

  props: {
    commitHasFailed: {
      type: Boolean,
      default: false,
    },
    handleError: {
      type: Function,
      required: true,
    },
  },

  computed: {
    ...mapState(ApplicationModel, [
      'certOrKeyHasChanged',
      'isScimActive',
      'item',
      'template',
    ]),
    ...mapGetters(ApplicationModel, [
      'isItemBeingCreated',
    ]),

    appSingleSignOnStatusProps() {
      const label = 'Integration Status';
      const { status } = this.template;

      if ([EOL, EOS].includes(status)) {
        const { appEOLorEOS } = KbArticleUrls;
        const subtextColor = StatusColorNames.info;
        let color;
        let subtext;

        if (status === EOL) {
          color = StatusColorNames.critical;
          subtext = `End of Life. [Learn more.](${appEOLorEOS})`;
        } else {
          color = StatusColorNames.warning;
          subtext = `End of Support. [Learn more.](${appEOLorEOS})`;
        }

        return {
          color,
          label,
          subtext,
          subtextColor,
        };
      }

      return {
        color: this.item.active ? StatusColorNames.active : StatusColorNames.critical,
        label,
      };
    },

    expirationDate() {
      return this.item.sso.idpCertExpirationAt ? this.item.sso.idpCertExpirationAt : null;
    },

    hasIdpPrivateKeyCertificate() {
      return this.isItemBeingCreated || this.certOrKeyHasChanged
        ? !!this.appmModelConfig.idpPrivateKey.value
        : !!this.appmModelConfig.idpCertificate.value;
    },

    idpCertificateStatusColor() {
      if (!this.isCertExpired && !this.isCertAboutToExpire) {
        return StatusColorNames.active;
      }

      return this.isCertExpired ? StatusColorNames.critical : StatusColorNames.warning;
    },

    idpCertificateStatusProps() {
      if (this.isItemBeingCreated || isOIDC(this.item) || !this.item?.sso?.type) return null;

      const componentConfig = this.getIdpCertificateBaseProps(
        this.expirationDate,
        this.appmHasIdpCertificate,
      );

      componentConfig.menuSections = [{
        menuItems: [{
          label: 'Regenerate certificate',
          action: this.regenerateCert,
        },
        {
          label: 'Download certificate',
          action: this.downloadIdpCertificate,
        },
        {
          label: 'Upload new certificate',
          action: this.uploadcertInputClick,
        }],
      }];

      return componentConfig;
    },

    idpPrivateKeyCertificateStatusProps() {
      if (isOIDC(this.item) || !this.item?.sso?.type) return null;

      const componentConfig = {
        color: this.hasIdpPrivateKeyCertificate
          ? StatusColorNames.active
          : StatusColorNames.info,
        label: 'IDP Private Key Valid',
        menuSections: [
          {
            menuItems: [
              {
                label: 'Upload IDP Private Key',
                action: this.uploadprivatekeyClick,
              },
            ],
          },
        ],
      };

      if (this.commitHasFailed && !this.hasIdpPrivateKeyCertificate) {
        componentConfig.label = 'Private Key Required';
        componentConfig.color = StatusColorNames.critical;
      }

      return componentConfig;
    },

    isCertAboutToExpire() {
      return !this.isCertExpired
        && DateUtil.daysUntil(this.expirationDate) <= 61;
    },

    isCertExpired() {
      return DateUtil.hasExpired(this.expirationDate);
    },
  },

  methods: {
    ...mapActions(ApplicationModel, [
      'saveItem',
      'setCertOrKeyHasChanged',
      'setConfig',
      'setItem',
    ]),
    ...mapActions(notificationModule, [
      'triggerNotification',
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    dataKeyChangedReadFile(event, reader) {
      return new Promise((resolve, reject) => {
        const element = event.target;
        const isToggle = !!element.attributes['data-is-toggle'];
        const key = element.attributes['data-key'].value;
        const config = cloneDeep(this.item.config);
        const keyInformation = config[key];

        if (isToggle && keyInformation && keyInformation.toggle) {
          keyInformation.toggle.value = element.checked;
          this.setConfig(config);
        } else if (keyInformation && keyInformation.type === 'file') {
          // Prepare the reader for persistence of the base64 file...
          // eslint-disable-next-line no-param-reassign
          reader.onload = (e) => {
            const rawContent = e.target.result;
            const base64Content = rawContent.substring(
              rawContent.indexOf(',') + 1,
            );

            resolve({
              key,
              base64Content, // keyInformation.value = base64Content;
              config,
              keyInformation,
            });
          };

          // eslint-disable-next-line no-param-reassign
          reader.onerror = () => {
            reject(Error(`Could not read ${keyInformation.label.replace(':', '')}`));
          };

          // Validate input file and bind to model...
          if (element && element.files.length > 0) {
            // If the file is too big, notify the user...
            const { size } = element.files[0]; // bytes
            if (size > 1024 * 1024) {
              reject(Error('Files uploaded must be smaller than 1 MB.'));
              return;
            }

            // File looks good, read in the contents and bind to model...
            reader.readAsDataURL(element.files[0]);
          }

          return;
        } else if (keyInformation && keyInformation.type === 'bool') {
          keyInformation.value = element.checked;
          this.setConfig(config);
          // Check if we have a special key/value key...
        } else if (ApplicationSSOModel.KEY_VALUE_INDEX_REGEX.test(key)) {
          const matches = key.match(ApplicationModel.KEY_VALUE_INDEX_REGEX);
          const name = matches[1]; // e.g. testKeys[3] => test
          const type = matches[2]; // e.g. testKeys[3] => Keys
          const index = matches[3]; // e.g. testKeys[3] => 3

          switch (type) {
            case ApplicationSSOModel.KEY_VALUE_KEYS_TYPE:
              config[name].value[index].name = element.value;
              this.setConfig(config);
              break;
            case ApplicationSSOModel.KEY_VALUE_VALUES_TYPE:
              config[name].value[index].value = element.value;
              this.setConfig(config);
              break;
            default:
              return;
          }
        } else {
          keyInformation.value = element.value;
          this.setConfig(config);
        }

        resolve({
          key,
          config,
          keyInformation,
        });
      });
    },

    getIdpCertificateBaseProps(expirationDate, hasIdpCertificate) {
      const props = {
        color: this.idpCertificateStatusColor,
        label: 'IDP Certificate Valid',
        subtext: undefined,
        subtextColor: undefined,
      };

      if (hasIdpCertificate) {
        props.subtext = `expires ${DateUtil.formatDate(expirationDate)}`;

        if (this.isCertExpired) {
          props.label = 'IDP Certificate Expired';
          props.subtext = `expired ${DateUtil.formatDate(expirationDate)}`;
          props.subtextColor = StatusColorNames.critical;
        } else {
          props.subtextColor = StatusColorNames.info;
        }
      } else if (this.commitHasFailed) {
        props.label = 'IDP Certificate Required';
        props.color = StatusColorNames.critical;
      }

      return props;
    },

    handleDataKeyChanged(event, item, reader = new FileReader()) {
      return this.dataKeyChangedReadFile(event, reader)
        .then((response) => {
          const idpCertificateKey = 'idpCertificate';

          const {
            key, config, base64Content, keyInformation,
          } = response;

          if (response.base64Content && key === idpCertificateKey) {
            try {
              import('node-forge').then(forge => {
                const { pki } = forge;
                pki.certificateFromPem(atob(base64Content));
              });
            } catch (e) {
              throw Error(`The value of ${keyInformation.label.replace(':', '')} should be a valid certificate`);
            }
          }

          if (!this.certOrKeyHasChanged) {
            if (key === idpCertificateKey) {
              config.idpPrivateKey.value = '';
            } else {
              config.idpCertificate.value = '';
            }

            this.setCertOrKeyHasChanged(true);
          }

          config[key].value = base64Content;
          this.setConfig(config);
          this.triggerSuccessNotification({
            title: `Successfully read ${keyInformation.label.replace(':', '')}`,
          });
        })
        .catch((response) => {
          this.triggerErrorNotification({
            title: 'Error',
            message: response.message,
          });
        });
    },

    regenerateCert() {
      const element = this.$el;

      element.attributes['data-state'] = DATA_SAVING_STATE;

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          headerText: 'Certificate regeneration',
          bodyText:
            'Are you sure you want to regenerate the certificate and the private key? You will need to download the new certificate and upload it to this application.',
          handleCancel: () => {
            element.attributes['data-state'] = DATA_READY_STATE;
            ModalEventBus.close();
          },
          handleContinue: () => this.regenerateCertModalHandleContinue(),
          showClose: true,
          kbLink: KbArticleUrls.managingApplicationIDPCertificate,
        },
      });
    },

    regenerateCertModalHandleContinue() {
      this.$el.attributes['data-state'] = DATA_READY_STATE;
      ModalEventBus.close();

      const config = cloneDeep(this.item.config);
      config.idpCertificate.value = '';
      config.idpPrivateKey.value = '';

      this.setCertOrKeyHasChanged(false);
      this.setConfig(config);
      this.saveItem()
        .then(item => this.setItem({ item }))
        .then(this.regenerateCertSuccess)
        .catch(this.handleError);
    },

    regenerateCertSuccess(model) {
      const url = this.generateBlobUrl(
        this.appmIdpCertificate,
        this.appmIdpCertificateDownloadName,
      );
      this.setConfig(model.config);
      this.triggerNotification({
        title: 'Certificate was successfully generated',
        component: {
          template: `<a download="${this.appmIdpCertificateDownloadName}" href="${url}">Download</a>`,
        },
        showCloseButton: true,
      });
    },

    uploadcertInputClick() {
      this.$refs.uploadcertInput.click();
    },

    uploadprivatekeyClick() {
      this.$refs.uploadprivatekey.click();
    },
  },
};
</script>

<style module>
.list {
  display: inline-block;
  padding-left: 23px;
  text-align: left;
}

.header {
  font-size: 16px;
  font-weight: 800;
}

.noneDisplayWrapper {
  display: none;
}

.statusItem {
  list-style-type: none;
}
</style>
